/**
 * SimplifiedPlaybackPage - シンプルな再生画面
 * 1枚の画像表示と「戻る」「進む」ボタンのみ
 * フェードトランジション付き
 * 
 * React SuspenseとTanStack Queryを使用したデータ取得
 */

import { useEffect, useRef, useCallback, Suspense, useMemo, useState } from 'react'
import { useStore, useSelectedSetId } from '../store'
import { useSuspenseSetData } from '../features/sets/hooks/useSuspenseSetData'
import { ErrorBoundary } from '../components/ErrorBoundary'

// Loading component for Suspense
function LoadingFallback() {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 border-4 border-gray-600 border-t-white rounded-full animate-spin" />
        <p className="text-gray-400">読み込み中...</p>
      </div>
    </div>
  )
}

// Main playback component
function PlaybackContent() {
  // Suspense対応のデータ取得
  const selectedSetId = useSelectedSetId()
  const { set: currentSet, imageUrls } = useSuspenseSetData(selectedSetId)
  
  // ローカル状態で現在のインデックスを管理（無限ループを防ぐため）
  const [localCurrentIndex, setLocalCurrentIndex] = useState(0)
  
  // ストアアクションを取得
  const navigateTo = useStore(state => state.navigateTo)
  
  // プリロード用キャッシュ
  const imageCache = useRef<Map<string, string>>(new Map())
  
  // 現在の画像URLを取得
  const currentImageUrl = useMemo(() => {
    if (imageUrls.length === 0 || localCurrentIndex >= imageUrls.length) return ''
    return imageUrls[localCurrentIndex] || ''
  }, [imageUrls, localCurrentIndex])
  
  // ナビゲーション関数
  const nextImage = useCallback(() => {
    if (!currentSet || imageUrls.length === 0) return
    const nextIndex = currentSet.type === 'circular'
      ? (localCurrentIndex + 1) % imageUrls.length
      : Math.min(localCurrentIndex + 1, imageUrls.length - 1)
    setLocalCurrentIndex(nextIndex)
  }, [currentSet, imageUrls.length, localCurrentIndex])
  
  const previousImage = useCallback(() => {
    if (!currentSet || imageUrls.length === 0) return
    const prevIndex = currentSet.type === 'circular'
      ? (localCurrentIndex - 1 + imageUrls.length) % imageUrls.length
      : Math.max(0, localCurrentIndex - 1)
    setLocalCurrentIndex(prevIndex)
  }, [currentSet, imageUrls.length, localCurrentIndex])
  
  // 画像のプリロード
  useEffect(() => {
    if (!currentSet || imageUrls.length === 0) return
    
    // 現在の画像をキャッシュに保存
    const currentUrl = imageUrls[localCurrentIndex]
    if (currentUrl && !imageCache.current.has(`img-${localCurrentIndex}`)) {
      imageCache.current.set(`img-${localCurrentIndex}`, currentUrl)
    }
    
    // 次の画像をプリロード
    const nextIndex = currentSet.type === 'circular' 
      ? (localCurrentIndex + 1) % imageUrls.length 
      : Math.min(localCurrentIndex + 1, imageUrls.length - 1)
    
    if (nextIndex !== localCurrentIndex && imageUrls[nextIndex]) {
      const nextUrl = imageUrls[nextIndex]
      if (!imageCache.current.has(`img-${nextIndex}`)) {
        const img = new Image()
        img.src = nextUrl
        img.onload = () => {
          imageCache.current.set(`img-${nextIndex}`, nextUrl)
        }
      }
    }
    
    // 前の画像もプリロード
    const prevIndex = currentSet.type === 'circular'
      ? (localCurrentIndex - 1 + imageUrls.length) % imageUrls.length
      : Math.max(0, localCurrentIndex - 1)
      
    if (prevIndex !== localCurrentIndex && imageUrls[prevIndex]) {
      const prevUrl = imageUrls[prevIndex]
      if (!imageCache.current.has(`img-${prevIndex}`)) {
        const img = new Image()
        img.src = prevUrl
        img.onload = () => {
          imageCache.current.set(`img-${prevIndex}`, prevUrl)
        }
      }
    }
  }, [localCurrentIndex, imageUrls, currentSet?.type])
  
  // 戻るボタンの処理
  useEffect(() => {
    const state = { page: 'simplified-playback' }
    window.history.pushState(state, '', null)
    
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault()
      navigateTo('decks')
    }
    
    window.addEventListener('popstate', handlePopState)
    
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [navigateTo])
  
  // キーボード操作
  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      navigateTo('decks')
    } else if (e.key === 'ArrowLeft') {
      previousImage()
    } else if (e.key === 'ArrowRight') {
      nextImage()
    }
  }, [navigateTo, previousImage, nextImage])
  
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [handleKeyPress])
  
  // ボタンの無効化状態を判定（メモ化）
  const { canGoPrevious, canGoNext } = useMemo(() => {
    const isFirstImage = localCurrentIndex === 0
    const isLastImage = localCurrentIndex === imageUrls.length - 1
    const isCircular = currentSet?.type === 'circular'
    return {
      canGoPrevious: isCircular || !isFirstImage,
      canGoNext: isCircular || !isLastImage
    }
  }, [localCurrentIndex, imageUrls.length, currentSet?.type])
  
  // ナビゲーションハンドラー（メモ化）
  const handlePrevious = useCallback(() => {
    if (canGoPrevious) {
      previousImage()
    }
  }, [canGoPrevious, previousImage])
  
  const handleNext = useCallback(() => {
    if (canGoNext) {
      nextImage()
    }
  }, [canGoNext, nextImage])
  
  const handleGoToDecks = useCallback(() => {
    navigateTo('decks')
  }, [navigateTo])
  
  // エラー・空の状態
  if (!currentSet || imageUrls.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 mb-4">画像がありません</p>
          <button
            onClick={handleGoToDecks}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
          >
            デッキ一覧に戻る
          </button>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* 画像表示エリア */}
      <div className="flex-1 relative overflow-hidden">
        {currentImageUrl && (
          <img
            key={`image-${localCurrentIndex}`} // シンプルで安定したkeyを使用
            src={currentImageUrl}
            alt={`画像 ${localCurrentIndex + 1}`}
            className="absolute inset-0 w-full h-full object-contain animate-fade-in"
          />
        )}
        
        {/* 進捗表示 */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full pointer-events-none">
          <span className="text-sm">
            {localCurrentIndex + 1} / {imageUrls.length}
          </span>
        </div>
      </div>

      {/* コントロール */}
      <div className="flex items-center justify-between p-4 bg-gray-900 border-t border-gray-800">
        {/* 戻るボタン */}
        <button
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          aria-label="前の画像"
          className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            !canGoPrevious
              ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
              : 'bg-gray-700 hover:bg-gray-600 text-white'
          }`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          戻る
        </button>

        {/* ホームボタン */}
        <button
          onClick={handleGoToDecks}
          aria-label="デッキ一覧に戻る"
          className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </button>

        {/* 進むボタン */}
        <button
          onClick={handleNext}
          disabled={!canGoNext}
          aria-label="次の画像"
          className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            !canGoNext
              ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
              : 'bg-gray-700 hover:bg-gray-600 text-white'
          }`}
        >
          進む
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  )
}

// メインコンポーネント（ErrorBoundaryとSuspenseでラップ）
export function SimplifiedPlaybackPage() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <PlaybackContent />
      </Suspense>
    </ErrorBoundary>
  )
}